import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Authentication } from '../models/authentication';
import { UserService } from '../services/user.service';
import { NotificationService } from '../services/notification.service';
import { User } from '../models/user';
import { Extensions } from '../helpers/extensions';
import { SidenavItem } from '../view-models/sidenav-item';
import { Subscription, forkJoin } from 'rxjs';
import { SignalrService } from '../services/signalr.service';
import { Encryptions } from '../helpers/encryptions';
import { MessageService } from '../services/message.service';
import { SidenavParent } from '../view-models/sidenav-parent';
import { ViewTermsOfUseComponent } from '../compliance/terms-of-use/view-terms-of-use/view-terms-of-use.component';
import { ComplianceService } from '../services/compliance.service';
import { MatDialog } from '@angular/material/dialog';
import { Permissions, Role } from '../models/role';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  loggedUser: User;
  authUser: Authentication;
  notificationSubscription: Subscription;
  messageSubscription: Subscription;
  notificationCount: number = 0;
  allowSidenavAccess: boolean;
  allowAdminTabsAccess: boolean;
  allowMessagesAccess: boolean;

  sidenavParents: SidenavParent[] = [
    new SidenavParent('Administração', [
      new SidenavItem('Usuários', 'groups_2', '/admin/users', true),
      new SidenavItem('Papéis', 'badge', '/admin/roles', true),
      new SidenavItem('Cooperados', 'diversity_3', '/admin/associates', true),
      new SidenavItem('Famílias', 'family_restroom', '/admin/associate-groups', true),
      new SidenavItem('Jobs', 'event_repeat', '/admin/jobs', true),
      new SidenavItem('Parâmetros', 'tune', '/admin/parameters', true),
      new SidenavItem('Versões do App', 'widgets', '/admin/app-versions', true),
    ]),
    new SidenavParent('Cadastros', [
      new SidenavItem('Itens', 'category', '/entries/items'),
      new SidenavItem('Safras', 'calendar_month', '/entries/harvests'),
      new SidenavItem('Códigos Vitícola', 'document_scanner', '/entries/viticulture-registrations'),
      new SidenavItem('Tabelas de Preço', 'point_of_sale', '/entries/price-lists', true),
    ]),
    new SidenavParent('Propriedades', [
      new SidenavItem('Áreas', 'forest', '/properties/areas'),
      new SidenavItem('Produção', 'agriculture', '/properties/production'),
      new SidenavItem('Maquinário', 'precision_manufacturing', '/properties/machinery'),
      new SidenavItem('Checklists', 'playlist_add_check', '/properties/checklists', false, ['/properties/checklists', '/properties/checklist-parameters']),
      new SidenavItem('Regulagens', 'plumbing', '/properties/pulverizers/setups', false, ['/properties/pulverizers']),
      new SidenavItem('Pedidos', 'shopping_cart', '/properties/orders'),
      new SidenavItem('Visitas Técnicas', 'hail', '/properties/technical-visits'),
      new SidenavItem('Cadernos de campo', 'auto_stories', '/properties/field-journal-activities'),
    ]),
    new SidenavParent('Relatórios', [
      new SidenavItem('Notas de Safra', 'newspaper', '/reports/invoices'),
      new SidenavItem('Relatório de Safra', 'equalizer', '/reports/harvest-report'),
      new SidenavItem('Financeiro', 'attach_money', '/reports/financial-transactions'),
      new SidenavItem('Produtividade', 'trending_up', '/reports/productivity-report'),
      new SidenavItem('Extrato de Safra', 'receipt_long', '/reports/harvest-statements'),
    ]),
    new SidenavParent('Documentos', [
      new SidenavItem('Publicações Legais', 'history_edu', '/documents/legal-posts'),
      new SidenavItem('DAP', 'folder_shared', '/documents/dap'),
    ]),
    new SidenavParent('Compliance', [
      new SidenavItem('Termos de Aceite', 'draw', '/compliance/acceptance-terms', true),
      new SidenavItem('Termos de Uso', 'verified_user', '/compliance/terms-of-use', true),
      new SidenavItem('Vistorias', 'policy', '/compliance/inspections'),
      new SidenavItem('Avaliações de Risco', 'flag', '/compliance/risk-ratings', true),
    ]),
  ];

  activeItem: SidenavItem;
  collapsed = this.readCollapsed();
  search = '';

  @ViewChild('sidenav') sidenav;

  constructor(private userService: UserService, private router: Router, private notificationService: NotificationService,
    private signalRService: SignalrService, private messageService: MessageService, private complianceService: ComplianceService,
    private dialog: MatDialog) { }

  ngOnInit() {
    this.setActiveRoute();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.setActiveRoute());

    this.notificationSubscription = this.notificationService.notificationCount.subscribe(count => {
      this.notificationCount = count;
    });

    this.userService.authentication.subscribe({
      next: authentication => {
        this.authUser = authentication;
        
        if (this.authUser != null) {
          this.allowSidenavAccess = this.authUser.isAssociate || Extensions.isUserInAnyRole(this.authUser, Role.getAllowedRoles(Permissions.ViewSidenavMenu));
          this.allowAdminTabsAccess = !this.authUser.isAssociate && Extensions.isUserInAnyRole(this.authUser, Role.getAllowedRoles(Permissions.ViewAdminTabs));
          this.allowMessagesAccess = this.authUser.isAssociate || Extensions.isUserInAnyRole(this.authUser, Role.getAllowedRoles(Permissions.AccessMessages));
          this.getUserImage(this.authUser);
          this.updateSidenavWidth();

          this.sidenavParents = this.sidenavParents.filter(s => !s.adminsOnlyTab || this.allowAdminTabsAccess);
        }
      },
      error: error => {
        this.notificationService.error(error);
      }
    });

    const notificationsRequest = this.notificationService.getNotifications({ unreadOnly: true });
    const termsOfUseRequest = this.complianceService.getTermsOfUse({ activeOnly: true });

    let requests = this.authUser.isAssociate ? [notificationsRequest, termsOfUseRequest] : [notificationsRequest];

    forkJoin(requests).subscribe({
      next: data => {
        this.notificationService.notificationCountSubject.next(data[0]?.length);

        if (data[1] != null && data[1].length > 0) {
          const dialogRef = this.dialog.open(ViewTermsOfUseComponent, { panelClass: 'dialog-extra-large', disableClose: true, data: { termsOfUse: data[1] } });

          dialogRef.afterClosed().subscribe({
            next: (termsOfUse: number[]) => {
              if (termsOfUse?.length > 0) {
                this.complianceService.acceptTermsOfUse({ acceptedTermsOfUse: termsOfUse }).subscribe({
                  error: error => {
                    this.notificationService.error(error);
                  }
                });
              }
            }
          });
        }
      },
      error: error => {
        this.notificationService.error(error);
      }
    });

    this.messageSubscription = this.signalRService.messageSubscription.subscribe({
      next: subscription => {
        if (subscription) {
          if (this.loggedUser.id == subscription.recipientID) {
            this.signalRService.getHubConnection.send('PongMessageReceived', this.loggedUser.id);

            if (this.router.url != '/messaging') {
              this.messageService.getChats().subscribe({
                next: chats => {
                  var chat = chats.find(c => c.participantID == subscription.senderID);

                  if (chat && !chat.isLastMessageDeleted) {
                    this.notificationService.messagePopUp(chat.participant,
                      Encryptions.decryptAES(chat.lastMessage, chat.isLastSender ? this.loggedUser.id : chat.participantID).content,
                      { participantID: subscription.senderID });
                  }
                },
                error: error => {
                  this.notificationService.error(error);
                }
              });
            }
          }
        }
      }
    });

    this.signalRService.startConnection();
    this.signalRService.addEventListener();
  }

  ngOnDestroy() {
    this.messageSubscription.unsubscribe();
    this.notificationSubscription.unsubscribe();
    this.signalRService.endConnection();
    document.documentElement.style.removeProperty('--sidenav-width');
  }

  getUserImage(authUser?: Authentication) {
    if (authUser == null) {
      return;
    }

    this.userService.getUserByID({ ID: authUser?.userID }).subscribe({
      next: user => {
        this.loggedUser = user;
        this.sidenavParents.forEach(p => p.items = p.items.filter(p => !p.adminsOnly || this.allowAdminTabsAccess))
        this.loggedUser.image = user.image?.length > 0 ? Extensions.convertImage(user.image) : null;
      },
      error: () => {
        this.loggedUser = null;
      }
    });
  }

  navigate(item) {
    this.router.navigate([item.route]);
  }

  // Sections keep only the items whose title matches the search, ignoring case and accents
  get filteredParents(): SidenavParent[] {
    const term = this.normalize(this.search);

    if (this.collapsed || term.length == 0) {
      return this.sidenavParents;
    }

    return this.sidenavParents
      .map(parent => ({ ...parent, items: parent.items.filter(item => this.normalize(item.title).includes(term)) }))
      .filter(parent => parent.items.length > 0);
  }

  // The filtered sections are rebuilt on every check, so the lists track by title to keep their elements;
  // otherwise a button is recreated between mousedown and click and the click is lost
  trackByTitle(index: number, entry: SidenavParent | SidenavItem): string {
    return entry.title;
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
    this.search = '';
    this.updateSidenavWidth();

    try {
      localStorage.setItem('sidenav-collapsed', String(this.collapsed));
    }
    catch { }
  }

  // In the collapsed rail the search box is only an icon, so clicking it opens the menu first
  expandToSearch(input: HTMLInputElement) {
    if (this.collapsed) {
      this.toggleCollapsed();
      setTimeout(() => input.focus());
    }
  }

  // Pages with fixed elements offset them by this width, so the sidebar doesn't cover them
  private updateSidenavWidth() {
    const width = !this.allowSidenavAccess ? 0 : this.collapsed ? 72 : 260;
    document.documentElement.style.setProperty('--sidenav-width', `${width}px`);
  }

  private readCollapsed(): boolean {
    try {
      return localStorage.getItem('sidenav-collapsed') == 'true';
    }
    catch {
      return false;
    }
  }

  private normalize(text: string): string {
    return (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  // The active item is the one whose paths hold the current URL, preferring the longest match,
  // so subpages (e.g. /properties/machinery/setups) highlight their parent item
  setActiveRoute() {
    const url = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
    let bestLength = -1;

    this.activeItem = null;

    for (const item of this.sidenavParents.reduce((items, parent) => items.concat(parent.items), [] as SidenavItem[])) {
      for (const path of item.paths.filter(path => url == path || url.startsWith(path + '/'))) {
        if (path.length > bestLength) {
          bestLength = path.length;
          this.activeItem = item;
        }
      }
    }
  }

  isActive(item: SidenavItem): boolean {
    return item == this.activeItem;
  }

  logout() {
    this.userService.logout();
  }
}
